import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="studio-finder"
export default class extends Controller {
  static targets = ['input', 'results', 'form'];
  static values = {
    url: String,
    nearestHeader: String,
    nearHeader: String,
    none: String,
    km: String,
    studioCta: String,
    onlocationUrl: String,
    onlocationLabel: String
  };

  connect() {
    this.debounceTimer = null;
    this.latestQuery = '';
    this.latestPayload = null;
    this.boundOutsideClick = this.onOutsideClick.bind(this);
    document.addEventListener('click', this.boundOutsideClick);
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
    document.removeEventListener('click', this.boundOutsideClick);
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(), 250);
  }

  onFocus() {
    if (this.inputTarget.value.trim()) this.search();
  }

  async onSubmit(event) {
    event.preventDefault();
    const query = this.inputTarget.value.trim();
    if (!query) {
      this.close();
      return;
    }

    const payload = await this.fetchResults(query);
    this.latestPayload = payload;

    if (payload.direct_count === 1) {
      const match = payload.results.find((item) => item.direct);
      if (match?.url) {
        window.location.href = match.url;
        return;
      }
    }

    this.render(payload, query);
  }

  async search() {
    const query = this.inputTarget.value.trim();
    this.latestQuery = query;

    if (!query) {
      this.close();
      return;
    }

    const payload = await this.fetchResults(query);
    if (query !== this.latestQuery) return;

    this.latestPayload = payload;
    this.render(payload, query);
  }

  async fetchResults(query) {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('q', query);

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      return { query, located: false, direct_count: 0, results: [] };
    }

    return response.json();
  }

  render(payload, query) {
    const results = payload.results || [];

    if (!results.length) {
      // Incomplete queries (e.g. "1") are not located — keep dropdown closed.
      if (!payload.located) {
        this.close();
        return;
      }
      this.resultsTarget.innerHTML = `<div class="studio-finder__none">${this.noneMessage(query)}</div>`;
      this.open();
      return;
    }

    let html = '';
    // Only for a valid located query with no direct studio hit (not for partial PLZ like "1").
    if (!payload.direct_count && payload.located) {
      html += `<div class="studio-finder__none studio-finder__heading">${this.interpolate(
        this.nearestHeaderValue,
        query
      )}</div>`;
    }

    let nearSeparator = false;
    results.forEach((item) => {
      if (!item.direct && payload.direct_count && !nearSeparator) {
        nearSeparator = true;
        html += `<div class="studio-finder__none studio-finder__heading">${this.nearHeaderValue}</div>`;
      }
      html += this.resultLink(item);
    });

    this.resultsTarget.innerHTML = html;
    this.open();
  }

  resultLink(item) {
    const title = `${this.escape(item.city)} · ${this.escape(item.name)}`;
    const address = this.escape(item.address || '');
    const km = item.km != null ? `<em class="studio-finder__km">${this.kmLabel(item.km)}</em>` : '';
    const href = this.escape(item.url || '#');

    return `<a class="studio-finder__result" href="${href}">
      <span class="studio-finder__result-main">
        <b class="studio-finder__result-title">${title}</b>
        <span class="studio-finder__result-address">${address}</span>
      </span>
      <span class="studio-finder__result-meta">${km}<span class="studio-finder__result-cta">${this.escape(
        this.studioCtaValue
      )}</span></span>
    </a>`;
  }

  noneMessage(query) {
    const link = this.hasOnlocationUrlValue
      ? `<a href="${this.escape(this.onlocationUrlValue)}">${this.escape(
          this.onlocationLabelValue || 'On Location'
        )}</a>`
      : '';
    return this.interpolate(this.noneValue, query).replace('{link}', link);
  }

  kmLabel(km) {
    if (km < 1) return '&lt; 1 km';
    return this.kmValue.replace('%{km}', String(Math.round(km)));
  }

  interpolate(template, query) {
    return this.escape(template || '')
      .replace('%{q}', this.escape(query))
      .replace('{q}', this.escape(query));
  }

  escape(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  open() {
    this.resultsTarget.classList.add('is-open');
  }

  close() {
    this.resultsTarget.classList.remove('is-open');
    this.resultsTarget.innerHTML = '';
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close();
  }
}
